import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaPlus,
  FaTrashAlt,
  FaMapMarkedAlt,
  FaUserTie,
  FaCity,
  FaSave,
  FaBuilding,
  FaUsers,
  FaProjectDiagram,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import TurkeyRegionMap from './TurkeyRegionMap';
import {
  normalizeCity,
  findRegionByCity,
  normalizeManagerIds,
  REGION_COLORS
} from './regionUtils';
import styles from './RegionManagementModal.module.css';

let tempIdCounter = 0;
const nextTempId = () => `temp-${++tempIdCounter}`;

const RegionManagementModal = ({ isOpen, onClose, employeesMap, onSaved, centers = [] }) => {
  const { isDarkMode } = useTheme();
  const [regions, setRegions] = useState([]);
  const [deletedRegionIds, setDeletedRegionIds] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [selectedCityName, setSelectedCityName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRegionIds, setExpandedRegionIds] = useState({});
  // centerId -> { workers: {id: name}, projects: {id: title} } derived from Projects
  const [centerAssignments, setCenterAssignments] = useState({});

  const employeeOptions = Object.entries(employeesMap)
    .map(([id, name]) => ({ id, name }))
    .filter((emp) => emp.name)
    .sort((a, b) => a.name.localeCompare(b.name, 'tr'));

  useEffect(() => {
    if (!isOpen) return;

    const fetchRegions = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'Regions'));
        const data = snapshot.docs.map((d) => {
          const region = { id: d.id, ...d.data() };
          return {
            ...region,
            cityManagerIds: region.cityManagerIds || {}
          };
        });
        setRegions(data);
        setDeletedRegionIds([]);
        setSelectedRegionId(data.length > 0 ? data[0].id : null);
        setSelectedCityName('');
      } catch (error) {
        console.error('Error fetching regions:', error);
        toast.error('Bölgeler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchRegions();
  }, [isOpen]);

  // Load project assignments once per open, to show worker/project counts per center
  useEffect(() => {
    if (!isOpen) return;

    const fetchAssignments = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'Projects'));
        const assignments = {};
        snapshot.docs.forEach((projectDoc) => {
          const data = projectDoc.data();
          const title = data.titleShort || data.titleLong || projectDoc.id;
          (data.employees || []).forEach((emp) => {
            if (!emp.centerId) return;
            if (!assignments[emp.centerId]) {
              assignments[emp.centerId] = { workers: {}, projects: {} };
            }
            if (emp.id) {
              assignments[emp.centerId].workers[emp.id] =
                `${emp.name || ''} ${emp.surname || ''}`.trim();
            }
            assignments[emp.centerId].projects[projectDoc.id] = title;
          });
        });
        setCenterAssignments(assignments);
      } catch (error) {
        console.error('Error fetching project assignments:', error);
      }
    };

    fetchAssignments();
  }, [isOpen]);

  const getCentersForCity = (cityName) => {
    const normalized = normalizeCity(cityName);
    return centers.filter((c) => normalizeCity(c['İl']) === normalized);
  };

  const getCentersForRegion = (region) =>
    (region.cities || []).flatMap((city) => getCentersForCity(city));

  // Unique worker/project counts across a set of centers
  const aggregateStats = (centerList) => {
    const workers = {};
    const projects = {};
    centerList.forEach((center) => {
      const a = centerAssignments[center.id];
      if (!a) return;
      Object.assign(workers, a.workers);
      Object.assign(projects, a.projects);
    });
    return {
      centerCount: centerList.length,
      workerCount: Object.keys(workers).length,
      projectCount: Object.keys(projects).length,
      projectNames: Object.values(projects)
    };
  };

  // City-level details for the map tooltip
  const getCityDetails = (cityName) => {
    const cityCenters = getCentersForCity(cityName);
    const stats = aggregateStats(cityCenters);
    return {
      ...stats,
      centers: cityCenters.map((center) => {
        const a = centerAssignments[center.id] || { workers: {}, projects: {} };
        return {
          id: center.id,
          name: center['Adı'] || '',
          workerCount: Object.keys(a.workers).length,
          projectCount: Object.keys(a.projects).length
        };
      })
    };
  };

  const toggleRegionExpand = (regionId, event) => {
    event.stopPropagation();
    setExpandedRegionIds((prev) => ({ ...prev, [regionId]: !prev[regionId] }));
  };

  const handleAddRegion = () => {
    const usedColors = regions.map((r) => r.color);
    const color =
      REGION_COLORS.find((c) => !usedColors.includes(c)) ||
      REGION_COLORS[regions.length % REGION_COLORS.length];

    const newRegion = {
      id: nextTempId(),
      isNew: true,
      name: `Bölge ${regions.length + 1}`,
      color,
      managerId: '',
      cityManagerIds: {},
      cities: []
    };
    setRegions((prev) => [...prev, newRegion]);
    setSelectedRegionId(newRegion.id);
    setSelectedCityName('');
  };

  const updateRegion = (regionId, changes) => {
    setRegions((prev) =>
      prev.map((r) => (r.id === regionId ? { ...r, ...changes } : r))
    );
  };

  const handleDeleteRegion = (regionId) => {
    const region = regions.find((r) => r.id === regionId);
    if (!region) return;

    setRegions((prev) => prev.filter((r) => r.id !== regionId));
    if (!region.isNew) {
      setDeletedRegionIds((prev) => [...prev, regionId]);
    }
    if (selectedRegionId === regionId) {
      setSelectedRegionId(null);
      setSelectedCityName('');
    }
  };

  const selectRegion = (regionId) => {
    setSelectedRegionId(regionId);
    setSelectedCityName('');
  };

  const selectCity = (regionId, cityName, event) => {
    event.stopPropagation();
    setSelectedRegionId(regionId);
    setSelectedCityName(cityName);
  };

  const addCityManager = (regionId, cityName, employeeId) => {
    if (!cityName || !employeeId) return;
    const cityKey = normalizeCity(cityName);

    setRegions((prev) =>
      prev.map((region) => {
        if (region.id !== regionId) return region;
        const currentManagers = normalizeManagerIds(region.cityManagerIds?.[cityKey]);
        if (currentManagers.includes(employeeId)) return region;

        return {
          ...region,
          cityManagerIds: {
            ...(region.cityManagerIds || {}),
            [cityKey]: [...currentManagers, employeeId]
          }
        };
      })
    );
  };

  const removeCityManager = (regionId, cityName, employeeId, event) => {
    event.stopPropagation();
    const cityKey = normalizeCity(cityName);

    setRegions((prev) =>
      prev.map((region) => {
        if (region.id !== regionId) return region;
        const nextManagers = normalizeManagerIds(region.cityManagerIds?.[cityKey])
          .filter((id) => id !== employeeId);
        const nextCityManagerIds = { ...(region.cityManagerIds || {}) };

        if (nextManagers.length > 0) {
          nextCityManagerIds[cityKey] = nextManagers;
        } else {
          delete nextCityManagerIds[cityKey];
        }

        return {
          ...region,
          cityManagerIds: nextCityManagerIds
        };
      })
    );
  };

  const handleCityClick = (city) => {
    if (!selectedRegionId) {
      toast.info('Önce sağdaki listeden bir bölge seçin veya yeni bölge ekleyin');
      return;
    }

    const normalized = normalizeCity(city.name);
    const currentRegion = findRegionByCity(regions, city.name);
    const alreadyInSelectedRegion = currentRegion?.id === selectedRegionId;
    setSelectedCityName(alreadyInSelectedRegion ? '' : city.name);

    setRegions((prev) =>
      prev.map((region) => {
        const withoutCity = (region.cities || []).filter(
          (c) => normalizeCity(c) !== normalized
        );
        const cityManagerIds = { ...(region.cityManagerIds || {}) };
        delete cityManagerIds[normalized];

        if (region.id === selectedRegionId) {
          // Toggle: remove if already in the selected region, otherwise add
          return {
            ...region,
            cityManagerIds,
            cities: alreadyInSelectedRegion ? withoutCity : [...withoutCity, city.name]
          };
        }
        // A city can belong to only one region — remove from others
        return { ...region, cityManagerIds, cities: withoutCity };
      })
    );
  };

  const getCityManagers = (region, cityName) =>
    normalizeManagerIds(region.cityManagerIds?.[normalizeCity(cityName)]);

  const handleSave = async () => {
    const unnamed = regions.find((r) => !r.name?.trim());
    if (unnamed) {
      toast.error('Tüm bölgelere bir isim verin');
      return;
    }

    setSaving(true);
    try {
      for (const regionId of deletedRegionIds) {
        await deleteDoc(doc(db, 'Regions', regionId));
      }

      for (const region of regions) {
        const payload = {
          name: region.name.trim(),
          color: region.color,
          managerId: region.managerId || '',
          cityManagerIds: Object.entries(region.cityManagerIds || {}).reduce((acc, [cityKey, managerIds]) => {
            const cityInRegion = (region.cities || []).some((city) => normalizeCity(city) === cityKey);
            const normalizedManagers = normalizeManagerIds(managerIds);
            if (cityInRegion && normalizedManagers.length > 0) {
              acc[cityKey] = normalizedManagers;
            }
            return acc;
          }, {}),
          cities: region.cities || [],
          updatedAt: new Date()
        };

        if (region.isNew) {
          await addDoc(collection(db, 'Regions'), {
            ...payload,
            createdAt: new Date()
          });
        } else {
          await updateDoc(doc(db, 'Regions', region.id), payload);
        }
      }

      toast.success('Bölgeler başarıyla kaydedildi');
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error('Error saving regions:', error);
      toast.error('Bölgeler kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const assignedCityCount = regions.reduce(
    (sum, r) => sum + (r.cities?.length || 0),
    0
  );

  return (
    <div className={`${styles.modalBackdrop} ${isDarkMode ? styles.darkMode : ''}`}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaMapMarkedAlt className={styles.titleIcon} />
            Bölge Yönetimi
          </h2>
          <button className={styles.closeButton} onClick={onClose} type="button">
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.mapSection}>
            <div className={styles.mapHint}>
              {selectedRegionId ? (
                <>
                  Haritadan illere tıklayarak{' '}
                  <strong style={{ color: regions.find((r) => r.id === selectedRegionId)?.color }}>
                    {regions.find((r) => r.id === selectedRegionId)?.name}
                  </strong>{' '}
                  bölgesine ekleyin / çıkarın
                </>
              ) : (
                'Bir bölge seçin, ardından haritadan illere tıklayarak atama yapın'
              )}
            </div>

            <TurkeyRegionMap
              regions={regions}
              selectedRegionId={selectedRegionId}
              onCityClick={handleCityClick}
              employeesMap={employeesMap}
              isDarkMode={isDarkMode}
              getCityDetails={getCityDetails}
            />

            <div className={styles.mapStats}>
              <span>
                <FaCity className={styles.statIcon} /> {assignedCityCount} / 81 il atandı
              </span>
              <span>
                <FaMapMarkedAlt className={styles.statIcon} /> {regions.length} bölge
              </span>
            </div>
          </div>

          <div className={styles.regionPanel}>
            <div className={styles.regionPanelHeader}>
              <h3>Bölgeler</h3>
              <button className={styles.addRegionButton} onClick={handleAddRegion} type="button">
                <FaPlus /> Yeni Bölge
              </button>
            </div>

            <div className={styles.regionList}>
              {loading ? (
                <div className={styles.emptyState}>Yükleniyor...</div>
              ) : regions.length === 0 ? (
                <div className={styles.emptyState}>
                  Henüz bölge tanımlanmadı. "Yeni Bölge" ile başlayın.
                </div>
              ) : (
                regions.map((region) => {
                  const regionCenters = getCentersForRegion(region);
                  const regionStats = aggregateStats(regionCenters);
                  const isExpanded = !!expandedRegionIds[region.id];
                  const cityList = [...(region.cities || [])].sort((a, b) => a.localeCompare(b, 'tr'));
                  const selectedCityBelongsToRegion = selectedCityName &&
                    cityList.some((cityName) => normalizeCity(cityName) === normalizeCity(selectedCityName));
                  const showCityManagerPanel = selectedRegionId === region.id && cityList.length > 0;
                  const selectedCityManagers = selectedCityBelongsToRegion
                    ? getCityManagers(region, selectedCityName)
                    : [];
                  const availableCityManagers = employeeOptions.filter(
                    (emp) => !selectedCityManagers.includes(emp.id)
                  );
                  return (
                  <div
                    key={region.id}
                    className={`${styles.regionCard} ${
                      selectedRegionId === region.id ? styles.regionCardSelected : ''
                    }`}
                    style={{ '--region-color': region.color }}
                    onClick={() => selectRegion(region.id)}
                  >
                    <div className={styles.regionCardTop}>
                      <input
                        type="color"
                        value={region.color}
                        className={styles.colorPicker}
                        title="Bölge rengi"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateRegion(region.id, { color: e.target.value })}
                      />
                      <input
                        type="text"
                        value={region.name}
                        className={styles.regionNameInput}
                        placeholder="Bölge adı"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateRegion(region.id, { name: e.target.value })}
                      />
                      <button
                        className={styles.deleteRegionButton}
                        title="Bölgeyi sil"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRegion(region.id);
                        }}
                      >
                        <FaTrashAlt />
                      </button>
                    </div>

                    <div className={styles.regionManagerRow}>
                      <FaUserTie className={styles.managerIcon} />
                      <select
                        value={region.managerId || ''}
                        className={styles.managerSelect}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateRegion(region.id, { managerId: e.target.value })}
                      >
                        <option value="">Bölge müdürü seçin</option>
                        {employeeOptions.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className={styles.regionCities}>
                      {(region.cities || []).length === 0 ? (
                        <span className={styles.noCities}>İl atanmadı</span>
                      ) : (
                        cityList.map((cityName) => {
                          const managerCount = getCityManagers(region, cityName).length;
                          const isSelectedCity = selectedRegionId === region.id &&
                            normalizeCity(cityName) === normalizeCity(selectedCityName);
                          return (
                            <button
                              key={cityName}
                              type="button"
                              className={`${styles.cityChip} ${isSelectedCity ? styles.cityChipSelected : ''}`}
                              title="Bu il için bölge sorumlularını yönet"
                              onClick={(e) => selectCity(region.id, cityName, e)}
                            >
                              {cityName}
                              {managerCount > 0 && (
                                <span className={styles.cityManagerCount}>{managerCount}</span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>

                    {showCityManagerPanel && (
                      <div className={styles.cityManagerPanel} onClick={(e) => e.stopPropagation()}>
                        {selectedCityBelongsToRegion ? (
                          <>
                            <div className={styles.cityManagerHeader}>
                              <span>{selectedCityName} bölge sorumluları</span>
                              <small>
                                {selectedCityManagers.length > 0
                                  ? `${selectedCityManagers.length} sorumlu`
                                  : 'İl özelinde atama yok'}
                              </small>
                            </div>

                            <div className={styles.cityManagerChips}>
                              {selectedCityManagers.length === 0 ? (
                                <span className={styles.cityManagerFallback}>
                                  {region.managerId
                                    ? `Bölge geneli sorumlusu kullanılır: ${employeesMap[region.managerId] || '-'}`
                                    : 'Bu il için henüz sorumlu atanmadı'}
                                </span>
                              ) : (
                                selectedCityManagers.map((managerId) => (
                                  <span key={managerId} className={styles.cityManagerChip}>
                                    {employeesMap[managerId] || managerId}
                                    <button
                                      type="button"
                                      onClick={(e) => removeCityManager(region.id, selectedCityName, managerId, e)}
                                      title="Sorumluyu kaldır"
                                    >
                                      <FaTimes />
                                    </button>
                                  </span>
                                ))
                              )}
                            </div>

                            <select
                              value=""
                              className={styles.cityManagerSelect}
                              disabled={availableCityManagers.length === 0}
                              onChange={(e) => addCityManager(region.id, selectedCityName, e.target.value)}
                            >
                              <option value="">
                                {availableCityManagers.length === 0 ? 'Eklenebilecek sorumlu kalmadı' : 'Sorumlu ekle'}
                              </option>
                              {availableCityManagers.map((emp) => (
                                <option key={emp.id} value={emp.id}>
                                  {emp.name}
                                </option>
                              ))}
                            </select>
                          </>
                        ) : (
                          <div className={styles.cityManagerHint}>
                            İl bazlı sorumlu eklemek için yukarıdan bir il seçin.
                          </div>
                        )}
                      </div>
                    )}

                    <div className={styles.regionStatsRow}>
                      <span className={styles.regionStat} title="Bölgedeki merkez sayısı">
                        <FaBuilding /> {regionStats.centerCount} merkez
                      </span>
                      <span className={styles.regionStat} title="Bölgedeki merkezlerde çalışan sayısı">
                        <FaUsers /> {regionStats.workerCount} çalışan
                      </span>
                      <span className={styles.regionStat} title="Bölgedeki merkezlerde yürüyen proje sayısı">
                        <FaProjectDiagram /> {regionStats.projectCount} proje
                      </span>
                      {regionCenters.length > 0 && (
                        <button
                          type="button"
                          className={styles.regionExpandButton}
                          onClick={(e) => toggleRegionExpand(region.id, e)}
                          title={isExpanded ? 'Merkez detaylarını gizle' : 'Merkez detaylarını göster'}
                        >
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      )}
                    </div>

                    {isExpanded && (
                      <div className={styles.regionCenterList}>
                        {regionCenters.map((center) => {
                          const a = centerAssignments[center.id] || { workers: {}, projects: {} };
                          const workerNames = Object.values(a.workers);
                          return (
                            <div key={center.id} className={styles.regionCenterItem}>
                              <div className={styles.regionCenterName}>
                                <FaBuilding className={styles.regionCenterIcon} />
                                {center['Adı']} <span className={styles.regionCenterCity}>({center['İl']})</span>
                              </div>
                              <div className={styles.regionCenterMeta}>
                                {workerNames.length} çalışan · {Object.keys(a.projects).length} proje
                              </div>
                              {workerNames.length > 0 && (
                                <div className={styles.regionCenterWorkers}>
                                  {workerNames.sort((x, y) => x.localeCompare(y, 'tr')).join(', ')}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            type="button"
            disabled={saving}
          >
            İptal
          </button>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            type="button"
            disabled={saving || loading}
          >
            <FaSave /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegionManagementModal;
